import { useState, useRef, useEffect } from 'react';
import { Check, Paintbrush } from 'lucide-react';
import styleSimple from '../../assets/templates/style-simple.png';
import styleBeemi from '../../assets/templates/style-beemi.png';
import styleDoodle from '../../assets/templates/style-doodle.png';
import styleRetro from '../../assets/templates/style-retro.png';
import styleNeon from '../../assets/templates/style-neon.png';

export type VisualStyle = 'simple' | 'beemi' | 'doodle' | 'retro' | 'neon';

interface StyleOption {
  id: VisualStyle;
  name: string;
  description: string;
  image: string;
}

interface StyleSelectorProps {
  selectedStyle: VisualStyle;
  onStyleChange: (style: VisualStyle) => void;
}

const STYLES: StyleOption[] = [
  {
    id: 'simple',
    name: 'Simple',
    description: 'Clean and minimal design with subtle colors',
    image: styleSimple
  },
  {
    id: 'beemi',
    name: 'Beemi',
    description: 'Vibrant and playful with bold colors',
    image: styleBeemi
  },
  {
    id: 'doodle',
    name: 'Doodle',
    description: 'Hand-drawn aesthetic with sketchy elements',
    image: styleDoodle
  },
  {
    id: 'retro',
    name: 'Retro',
    description: '8-bit pixel art with classic arcade vibes',
    image: styleRetro
  },
  {
    id: 'neon',
    name: 'Neon',
    description: 'Cyberpunk glow with dark backgrounds',
    image: styleNeon
  }
];

export default function StyleSelector({ 
  selectedStyle, 
  onStyleChange
}: StyleSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedStyleOption = STYLES.find(s => s.id === selectedStyle) || STYLES[1]; // Default to beemi

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (style: VisualStyle) => {
    onStyleChange(style);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-2 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-300 dark:border-dark-border"
        title={`Style: ${selectedStyleOption.name}`}
      >
        <Paintbrush className="w-5 h-5 text-gray-600 dark:text-dark-text-muted" />
        <img src={selectedStyleOption.image} alt={selectedStyleOption.name} className="w-6 h-6 rounded object-cover" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-300 dark:border-dark-border rounded-lg shadow-xl z-50 overflow-hidden">
          {STYLES.map((style) => (
            <button
              key={style.id}
              onClick={() => handleSelect(style.id)}
              className={`w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-dark-border last:border-b-0 ${
                selectedStyle === style.id ? 'bg-gray-50 dark:bg-gray-700/50' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <img src={style.image} alt={style.name} className="w-8 h-8 rounded flex-shrink-0 object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900 dark:text-dark-text">{style.name}</span>
                    {selectedStyle === style.id && (
                      <Check className="w-4 h-4 text-green-500" strokeWidth={2.5} />
                    )}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-dark-text-muted leading-relaxed">
                    {style.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

